import {
    Box,
    CircularProgress,
    CircularProgressProps,
    Fade
} from '@mui/material'

/**
 * Interface for LoadingIndicator component props, extending CircularProgressProps.
 *
 * @property {boolean} isLoading - Indicates whether the loading indicator should be displayed.
 * @property {boolean} [withBox] - Optional. Determines if the loading indicator should be wrapped in a box overlay.
 * @property {boolean} [darkBackground] - Optional. Sets the background of the box overlay to dark if true.
 * @property {boolean} [withoutDelay] - Optional. Removes transition delay if true.
 */
interface LoadingIndicatorProps extends CircularProgressProps {
    isLoading: boolean
    withBox?: boolean
    darkBackground?: boolean
    withoutDelay?: boolean
}

/**
 * Displays a loading indicator with optional box and delay.
 *
 * @param {boolean} isLoading - Determines if the loading indicator should be displayed.
 * @param {boolean} withBox - If true, wraps the indicator in a full-screen box.
 * @param {boolean} darkBackground - If true, uses a dark background for the box.
 * @param {boolean} withoutDelay - If true, shows the indicator without transition delay.
 * @param {...object} rest - Additional props passed to the CircularProgress component.
 * @returns {JSX.Element} A React component displaying a loading indicator.
 */
export default function LoadingIndicator({
    isLoading,
    withBox,
    darkBackground,
    withoutDelay,
    ...rest
}: LoadingIndicatorProps) {
    return (
        <Fade
            in={isLoading}
            style={{
                transitionDelay: isLoading && !withoutDelay ? '100ms' : '0ms'
            }}
            unmountOnExit
        >
            {withBox ? (
                <Box
                    sx={{
                        position: 'fixed',
                        zIndex: 9999,
                        width: '100%',
                        height: '100vh',
                        top: 0,
                        left: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        background: darkBackground
                            ? 'rgba(0,0,0, 0.2)'
                            : 'rgba(255,255,255,0.5)'
                    }}
                >
                    <CircularProgress className="loader" {...rest} />
                </Box>
            ) : (
                <CircularProgress className="loader" {...rest} />
            )}
        </Fade>
    )
}
